import React, { useState, useRef } from "react";
import { useApp, Reward } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { 
  Gift, 
  Plus, 
  Trash2, 
  Edit, 
  Award, 
  CheckCircle, 
  XCircle, 
  Calendar,
} from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const RewardsManager: React.FC = () => {
  const { rewards, createReward, updateReward, deleteReward, hasPermission } = useApp();
  const editCloseRef = useRef<HTMLButtonElement>(null);
  const createCloseRef = useRef<HTMLButtonElement>(null);
  
  const [newReward, setNewReward] = useState<Omit<Reward, "id" | "created">>({
    name: "",
    description: "",
    type: "visit_based",
    visitsRequired: 2,
    active: true
  });
  
  const [editingReward, setEditingReward] = useState<Reward | null>(null);
  const [deleteRewardId, setDeleteRewardId] = useState<string>("");
  
  const handleCreateReward = () => {
    if (!newReward.name) return;
    
    createReward(newReward);
    
    // Close the sheet using the ref
    if (createCloseRef.current) {
      createCloseRef.current.click();
    }
    
    // Reset form
    setNewReward({
      name: "",
      description: "",
      type: "visit_based",
      visitsRequired: 2,
      active: true
    });
  };
  
  const handleEditReward = () => {
    if (!editingReward?.id) return;
    
    updateReward(editingReward.id, {
      name: editingReward.name,
      description: editingReward.description,
      type: editingReward.type,
      visitsRequired: editingReward.visitsRequired,
      active: editingReward.active
    });
    
    // Close the sheet using the ref
    if (editCloseRef.current) {
      editCloseRef.current.click();
    }
    
    setEditingReward(null);
  };
  
  const handleDeleteReward = () => {
    if (!deleteRewardId) return;
    
    deleteReward(deleteRewardId);
    setDeleteRewardId("");
  };
  
  const startEdit = (reward: Reward) => {
    setEditingReward({ ...reward });
  };
  
  const hasEditPermission = hasPermission("manage_rewards");

  return (
    <Card className="border-t-4 border-t-restaurant-primary shadow-lg">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Gift className="h-5 w-5 text-restaurant-primary" />
          <CardTitle className="text-xl">Rewards Management</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {rewards.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground flex flex-col items-center gap-2">
            <Award className="h-12 w-12 text-muted-foreground/50" />
            <p className="text-lg">No rewards defined</p>
            {hasEditPermission && (
              <p className="text-sm text-muted-foreground">
                Start by creating your first reward
              </p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold">Reward</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  {hasEditPermission && (
                    <TableHead className="text-right font-semibold">Actions</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rewards.map((reward) => (
                  <TableRow key={reward.id} className="hover:bg-muted/30">
                    <TableCell className="font-medium">
                      <div>
                        <p className="font-semibold">{reward.name}</p>
                        <p className="text-sm text-muted-foreground">{reward.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Required visits per week: {reward.visitsRequired}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {reward.active ? (
                        <div className="flex items-center gap-1 text-restaurant-primary">
                          <CheckCircle className="h-4 w-4" />
                          <span>Active</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <XCircle className="h-4 w-4" />
                          <span>Inactive</span>
                        </div>
                      )}
                    </TableCell>
                    {hasEditPermission && (
                      <TableCell className="text-right space-x-1">
                        <Sheet>
                          <SheetTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => startEdit(reward)}
                              className="border-restaurant-primary/30 text-restaurant-primary"
                            >
                              <Edit className="h-3.5 w-3.5 mr-1" />
                              Edit
                            </Button>
                          </SheetTrigger>
                          <SheetContent className="overflow-y-auto">
                            {editingReward && (
                              <>
                                <SheetHeader>
                                  <SheetTitle className="flex items-center gap-2">
                                    <Gift className="h-5 w-5 text-restaurant-primary" />
                                    Edit Reward
                                  </SheetTitle>
                                  <SheetDescription>
                                    Update reward details
                                  </SheetDescription>
                                </SheetHeader>
                                <div className="py-6 space-y-5">
                                  <div className="space-y-2">
                                    <Label htmlFor="edit-reward-name">Reward Name</Label>
                                    <Input
                                      id="edit-reward-name"
                                      value={editingReward.name}
                                      onChange={(e) => setEditingReward({ 
                                        ...editingReward, 
                                        name: e.target.value 
                                      })}
                                      placeholder="e.g., Free Entrée"
                                      className="border-restaurant-border focus-visible:ring-restaurant-primary"
                                    />
                                  </div>
                                  
                                  <div className="space-y-2">
                                    <Label htmlFor="edit-reward-description">Description</Label>
                                    <Input
                                      id="edit-reward-description"
                                      value={editingReward.description}
                                      onChange={(e) => setEditingReward({
                                        ...editingReward,
                                        description: e.target.value
                                      })}
                                      placeholder="e.g., Get a free entrée when you dine in twice in the same week"
                                      className="border-restaurant-border focus-visible:ring-restaurant-primary"
                                    />
                                  </div>

                                  <div className="space-y-2">
                                    <Label htmlFor="edit-reward-visits">Required Visits per Week</Label>
                                    <Input
                                      id="edit-reward-visits"
                                      type="number"
                                      min="1"
                                      value={editingReward.visitsRequired}
                                      onChange={(e) => setEditingReward({
                                        ...editingReward,
                                        visitsRequired: Math.max(1, parseInt(e.target.value) || 1)
                                      })}
                                      className="border-restaurant-border focus-visible:ring-restaurant-primary"
                                    />
                                  </div>

                                  <div className="flex items-center space-x-2">
                                    <Switch
                                      id="edit-reward-active"
                                      checked={editingReward.active}
                                      onCheckedChange={(checked) => setEditingReward({
                                        ...editingReward,
                                        active: checked
                                      })}
                                      className="data-[state=checked]:bg-restaurant-primary"
                                    />
                                    <Label htmlFor="edit-reward-active">Active</Label>
                                  </div>

                                  <div className="pt-4 space-x-2 flex justify-end">
                                    <Button
                                      variant="outline"
                                      onClick={() => setEditingReward(null)}
                                      className="border-restaurant-primary/30"
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      onClick={handleEditReward}
                                      disabled={!editingReward.name}
                                      className="bg-restaurant-primary hover:bg-restaurant-primary-hover"
                                    >
                                      Save Changes
                                    </Button>
                                    <SheetClose ref={editCloseRef} className="hidden" />
                                  </div>
                                </div>
                              </>
                            )}
                          </SheetContent>
                        </Sheet>

                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDeleteRewardId(reward.id)}
                              className="border-destructive/30 text-destructive"
                            >
                              <Trash2 className="h-3.5 w-3.5 mr-1" />
                              Delete
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete this reward.
                                This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                              <AlertDialogCancel className="mt-0">Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={handleDeleteReward}
                                className="bg-restaurant-primary hover:bg-restaurant-primary-hover"
                              >
                                Yes, delete reward
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {hasEditPermission && (
          <div className="p-4 border-t">
            <Sheet>
              <SheetTrigger asChild>
                <Button className="bg-restaurant-primary hover:bg-restaurant-primary-hover">
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Reward
                </Button>
              </SheetTrigger>
              <SheetContent className="overflow-y-auto">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <Gift className="h-5 w-5 text-restaurant-primary" />
                    Create New Reward
                  </SheetTitle>
                  <SheetDescription>
                    Set up a new reward for your customers
                  </SheetDescription>
                </SheetHeader>
                <div className="py-6 space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="reward-name">Reward Name</Label>
                    <Input
                      id="reward-name"
                      value={newReward.name}
                      onChange={(e) => setNewReward({ ...newReward, name: e.target.value })}
                      placeholder="e.g., Free Entrée"
                      className="border-restaurant-border focus-visible:ring-restaurant-primary"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="reward-description">Description</Label>
                    <Input
                      id="reward-description"
                      value={newReward.description}
                      onChange={(e) => setNewReward({ ...newReward, description: e.target.value })}
                      placeholder="e.g., Get a free entrée when you dine in twice in the same week"
                      className="border-restaurant-border focus-visible:ring-restaurant-primary"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reward-visits">Required Visits per Week</Label>
                    <Input
                      id="reward-visits"
                      type="number"
                      min="1"
                      value={newReward.visitsRequired}
                      onChange={(e) => setNewReward({
                        ...newReward,
                        visitsRequired: Math.max(1, parseInt(e.target.value) || 1)
                      })}
                      className="border-restaurant-border focus-visible:ring-restaurant-primary"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="reward-active"
                      checked={newReward.active}
                      onCheckedChange={(checked) => setNewReward({
                        ...newReward,
                        active: checked
                      })}
                      className="data-[state=checked]:bg-restaurant-primary"
                    />
                    <Label htmlFor="reward-active">Active</Label>
                  </div>

                  <div className="pt-4 flex justify-end">
                    <Button
                      onClick={handleCreateReward}
                      disabled={!newReward.name}
                      className="bg-restaurant-primary hover:bg-restaurant-primary-hover"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create Reward
                    </Button>
                    <SheetClose ref={createCloseRef} className="hidden" />
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RewardsManager;
